import { useMemo } from 'react';
import { useApp } from '../AppProvider';
import { useAuth } from '../../lib/auth';
import { useLeadNoteStore } from '../../lib/stores/leadNoteStore';
import { usePartnerNoteStore } from '../../lib/stores/partnerNoteStore';
import { getCurrentUserOrganizationId } from '../../lib/stores/referenceDataCache';
import { useActivitySave } from '../shared/activity-drawer/useActivitySave';
import { resolveComposerEndDate } from '../shared/timeline/composerDueDate';
import { saveLeadNoteWithAttachments } from '../leads/leadDrawerWrites';
import type { EntityAdapter } from '../shared/activity-drawer/types';

/**
 * Gemello di `useLeadComposerSaves.ts` per il composer inline della cronologia di un
 * PARTNER (Task 1, piano "Partner drawer unificato"). Stessa forma di ritorno
 * (`{ saveNote, saveActivity }`), consumata dallo stesso composer host.
 *
 * Differenze deliberate rispetto al lead, per DESIGN (vedi task brief):
 * - Nota: passa comunque da `saveLeadNoteWithAttachments` (stessa funzione, stesso
 *   comportamento di allegati/toast) ma con `linkage: { partner_id }` — la scrittura
 *   finisce su `deal_notes.partner_id`. `createNote` è quello di `usePartnerNoteStore`,
 *   che ha una firma diversa da quello lead (richiede `organizationId`/`userId` invece
 *   di un `pipelineContext` opzionale): qui viene adattato alla stessa forma posizionale
 *   `(entityId, content, pipelineContext?)` attesa da `saveLeadNoteWithAttachments`.
 * - Allegati: `usePartnerNoteStore` non espone un `uploadNoteAttachment` (vedi commento
 *   in `PartnerNoteCard.tsx`). La funzione di `useLeadNoteStore` è generica — scrive solo
 *   su `note_attachments.note_id` e risolve l'utente/organizzazione internamente via
 *   `supabase.auth.getUser()`, senza mai toccare `lead_id` — quindi è riusabile TALE E
 *   QUALE anche per le note partner.
 * - Attività: NESSUN trigger di workflow (`afterActivitySaved` è un no-op: i partner non
 *   hanno automazioni collegate) e NESSUNA emissione `eventBus`, a differenza del lead.
 */
export function usePartnerComposerSaves(params: { partnerId: string; pipelineContext?: string }) {
  const { partnerId, pipelineContext } = params;
  const { toast } = useApp();
  const { user } = useAuth();
  const { uploadNoteAttachment } = useLeadNoteStore();
  const { createNote: createPartnerNote } = usePartnerNoteStore();

  const adapter: EntityAdapter = useMemo(
    () => ({
      kind: 'partner',
      entity: { id: partnerId },
      // Il core di `useActivitySave` usa `clientId` per clientData/attendees/evento: un
      // partner non è un cliente, quindi resta assente (nessun dato cliente da agganciare).
      clientId: undefined,
      entityUrl: () => `${window.location.origin}/partners/${partnerId}`,
      linkageFields: () => ({ partner_id: partnerId }),
      defaultAssigneeId: null,
      // Il composer non tocca mai fase/assegnatario dell'entità: nessuna validazione,
      // nessun commit (identico al lead — vedi useLeadComposerSaves.ts).
      validateEntityChanges: () => null,
      commitEntityChanges: async () => {},
      saveNote: async (html: string, files: File[]) =>
        saveLeadNoteWithAttachments({
          leadId: partnerId,
          html,
          files,
          pipelineContext,
          createNote: async (entityId: string, content: string) => {
            const organizationId = await getCurrentUserOrganizationId();
            if (!organizationId || !user?.id) return null;
            return createPartnerNote(entityId, content, organizationId, user.id);
          },
          uploadNoteAttachment,
          toast,
          linkage: { partner_id: partnerId },
        }),
      // Nessun trigger di workflow né eventBus per i partner: a differenza del lead
      // (`triggerLeadActivityWorkflows`), qui non c'è nulla da chiamare dopo il salvataggio.
      afterActivitySaved: async () => {},
    }),
    [partnerId, pipelineContext, createPartnerNote, uploadNoteAttachment, toast, user?.id]
  );

  const { saveActivity } = useActivitySave(adapter);

  const saveNote = async (html: string) => {
    await adapter.saveNote(html, []);
  };

  const saveActivityQuick = async (input: { title: string; type: string; dueDate: Date; description?: string }) => {
    const organizationId = await getCurrentUserOrganizationId();
    if (!organizationId) {
      toast.error('Organizzazione non disponibile: riprova');
      throw new Error('organization_id non disponibile');
    }
    const saved = await saveActivity({
      activityData: {
        title: input.title,
        type: input.type,
        status: 'pending',
        startDateTime: input.dueDate,
        endDateTime: resolveComposerEndDate(input.dueDate),
        // Attività veloce: sempre assegnata all'utente corrente (come per il lead).
        assigned_to: user?.id || null,
        description: input.description ?? '',
      },
      editingActivity: null,
      syncOptIn: false,
      meetingProvider: 'none',
      selectedAttendees: [],
      invitedCommercial: null,
      meetOrganizer: 'agent',
      includeDescriptionInEvent: false,
      organizationId,
      toast,
    });
    // `saveActivity` ha già mostrato il toast d'errore: il throw serve solo a far
    // conservare al composer il testo digitato.
    if (!saved) throw new Error("Salvataggio dell'attività fallito");
  };

  return { saveNote, saveActivity: saveActivityQuick };
}
